<template lang="pug">
.multiple-skus-add-to-cart
  .moq-warning(v-if="groupVariantsByRegion[REGION_TYPES.PFD]")
    el-alert.mb-3(
      v-if="remainQty(groupVariantsByRegion[REGION_TYPES.PFD]) > 0 && estimatedQuantity(groupVariantsByRegion[REGION_TYPES.PFD]) > 0"
      type="warning"
      size="small"
      :closable="false")
      .alert.pf-flex
        .notice(style="flex: 1;") Please add more cases to your order to meet the minimum number of cases required. This vendor may not fulfill if this minimum is not met. Thank you!
        .counter.ml-1
          strong {{ remainQty(groupVariantsByRegion[REGION_TYPES.PFD]) | number }}
          | &nbsp;{{ remainQty(groupVariantsByRegion[REGION_TYPES.PFD]) > 1 ? 'cases' : 'case' }}
          small more
  .skus-list
    .sku-item(
      v-for="v, vk in variants" :key="vk"
      :class="{selected: selectedId === variantId(v), selectable: !readOnly}"
      @click="updateSelectedSku(v)")
      .general
        .image(v-if="showImage")
          nuxt-link.linked(
            v-if="clickableImage"
            :to="$links.product({id: v.product_id, name: v.product_name, variant: variantName(v), variantId: variantId(v), brand: v.brand_name})"
            :title="variantName(v)"
            v-click="() => $amplitude.trackProduct(trackProps)")
            .contain(v-image="imageUrl(v)")
          .contain(v-image="imageUrl(v)" v-else)
        .preview
          .brand(v-if="showBrand")
            strong
              nuxt-link(
                v-if="showBrandUrl"
                :to="$links.brand({id: v.brand_id, name: v.brand_name})"
                v-text="v.brand_name"
                :title="v.brand_name"
                v-click="() => $amplitude.trackBrand(trackProps)")
              span(v-else v-text="v.brand_name")
          .product(v-if="showProduct")
            strong(v-if="showProductUrl")
              nuxt-link.linked(
                v-text="v.product_name"
                :to="$links.product({id: v.product_id, name: v.product_name, brand: v.brand_name})"
                :title="v.product_name"
                v-click="() => $amplitude.trackProduct(trackProps)")
            span(v-else v-text="v.product_name")
          .variant(v-if="showVariant")
            nuxt-link.linked(
              v-if="showVariantUrl"
              :to="$links.product({id: v.product_id, name: v.product_name, variant: variantName(v), variantId: variantId(v), brand: v.brand_name})"
              :title="variantName(v)"
              v-click="() => $amplitude.trackProduct(trackProps)") {{ variantName(v) }}
            span(v-else) {{ variantName(v) }}
          .variant-regions(v-if="showVariantRegions")
            .region-item(v-for="i, k in v.variants_regions" :key="k")
              .blocks
                .edt-piece.region {{ i.region_name }}
                .edt-piece.original-price(:class="{ 'line-through': v.promotion }") {{ i.case_price_cents | cents }}
                .edt-piece.discount-price(v-if="v.promotion") {{ i.case_price_cents | promotionPrice(v.promotion) }}
          .upc(v-if="showUpc") {{ v.upc }}
          .info-variant__name(v-if="showVariantName" v-text="variantName(v)")
          .flex.aic.fww
            .info-variant__id(v-if="showVariantId"
              v-tooltip="'Item code is a unique identifier for SKUs that helps you search products and order with ease.'") # {{ variantId(v) }}
            ExpressTag.express-tag(v-if="showBadge && v.is_warehoused_variant" v-tooltip="POD_EXPRESS_EXPLANATION")
          .info-variant__cases-per-master-carton(v-if="showCasesPerMasterCarton")
            span Cases per Master Carton:&nbsp;
              strong {{ v.cases_per_master_carton || 'N/A' }}
          .info-variant__state(v-if="showState")
            status-tag(:type="v.state")
          .upc(v-if="showUnitUPC")
            strong Unit UPC / EAN: {{ v.barcode_code }}
          .upc(v-if="showCaseUPC")
            strong Case UPC / EAN: {{ v.barcode_case_code }}
          .pack
            span.mr-1(v-if="showUnitPrice") {{ unitPrice(v) }}/unit
            span.case-unit(v-if="showCaseUnits") {{ caseUnits(v) | number }} {{ caseUnits(v) > 1 ? 'units' : 'unit' }}/case
        .price(v-if="isBuyer && showPrice")
          span.current(:class="{promoted: !!promotions(v).length}") {{ v.case_price_cents | promotionPrice(promotions(v)[0]) }}
          span.old(v-if="promotions(v).length") {{ v.case_price_cents | cents }}
          .quick-promotion-tag(v-if="promotions(v).length")
            PromotionTag(
              fontSize="11px"
              :showPopover="false"
              :type="promotions(v)[0].type | promotionType"
              :variants="[{ ...v, imageUrl: imageUrl(v), name: variantName(v)}]")
          PriceChangeStamp.ml-1(v-if="showPriceChange" :variant="v") &nbsp;

        .quantity-change(v-if="isBuyer && showQuantityChange" :ref="`quantityBox-${variantId(v)}`")
          el-input-number.pf-block(
            v-if="v.cartable"
            v-model="quantities[v.variants_region_id]"
            :min="0"
            size="small")
          .not-available.flex.aic.jcfc(v-else)
            span Not available
      .promotion-explained(v-if="isPriceDiscoverable && selectedId === variantId(v)")
        PromotionExplainedStamp(
          :product="product"
          :variant="v"
          :quantity="quantities[v.variants_region_id]")
  .actions(v-if="showActionBar")
    el-button.add-to-cart(
      v-if="isBuyer && showAddtoCartBtn"
      type="primary"
      :disabled="!submittable || adding"
      @click="addToCart")
      i.pf-icon.bx.bx-cart-alt.mr
      span Add to Cart
    .sample-request(v-if="(isBuyer || isHeadBuyer) && showRequestSample && !!product.sampleable")
      a.action.flex.linked(
        href="#"
        @click.prevent="requestSample")
        i.pf-icon.bx.bxs-eyedropper.bx-5.mr
        span Request Samples →
</template>

<script>
import { mapGetters } from 'vuex'
import { groupBy } from 'lodash'
import { REGION_TYPES, LIMIT_TYPES, POD_EXPRESS_EXPLANATION, POD_GROW_EXPLANATION } from '~/settings'
import PriceChangeStamp from '~/components/stamps/PriceChangeStamp'
import PromotionTag from '~/components/tags/PromotionTag'
import PromotionExplainedStamp from '~/components/stamps/PromotionExplainedStamp'
import ExpressTag from '~/components/tags/ExpressTag'
import GrowTag from '~/components/tags/GrowTag'

export default {
  components: {
    ExpressTag,
    GrowTag,
    PriceChangeStamp,
    PromotionExplainedStamp,
    PromotionTag
  },
  props: {
    variants: {
      required: true,
      type: Array
    },
    product: {
      required: true,
      type: Object,
      default: () => {}
    },
    selectedId: { type: Number, default: null },
    showImage: { type: Boolean },
    showPrice: { type: Boolean },
    readOnly: { type: Boolean },
    showBadge: { type: Boolean },
    showBrand: { type: Boolean },
    showBrandUrl: { type: Boolean },
    showProduct: { type: Boolean },
    showProductUrl: { type: Boolean },
    showVariant: { type: Boolean },
    showVariantUrl: { type: Boolean },
    showVariantName: { type: Boolean },
    showUpc: { type: Boolean },
    showUnitUPC: { type: Boolean },
    showCaseUPC: { type: Boolean },
    showCaseUnits: { type: Boolean },
    showUnitPrice: { type: Boolean },
    showPriceChange: { type: Boolean },
    showState: { type: Boolean },
    showVariantId: { type: Boolean },
    clickableImage: { type: Boolean },
    showVariantRegions: { type: Boolean },
    showCasesPerMasterCarton: { type: Boolean },
    showQuantityChange: { type: Boolean },
    showAddtoCartBtn: { type: Boolean },
    showRequestSample: { type: Boolean },
    trackProps: { type: [Object, String], default: '' }
  },
  data () {
    return {
      REGION_TYPES,
      POD_EXPRESS_EXPLANATION,
      POD_GROW_EXPLANATION,
      quantities: {},
      adding: false,
      selectedSku: this.variants.find(v => v.id === this.selectedId)
    }
  },
  computed: {
    ...mapGetters({
      apiRole: 'authentication/apiRole',
      isBuyer: 'authentication/isBuyer',
      isHeadBuyer: 'authentication/isHeadBuyer',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer'
    }),

    showActionBar () {
      return (this.isBuyer || this.isHeadBuyer) &&
        (this.showAddtoCartBtn || (this.showRequestSample && !!this.product.sampleable)) && this.variants.some(e => !e.__INACTIVE)
    },

    trackZone () {
      switch (this.$route?.name) {
        case '_product':
          return 'PRODUCT_DETAILS_PAGE_ADD_TO_CART_BUTTON'
        default:
          return `MAYBE_MISSING_ZONE_FROM_ROUTE_${this.$route?.name}`
      }
    },

    totalQuantitiesAddToCart () {
      return Object.values(this.quantities).reduce((sum, v) => sum + v, 0)
    },

    totalSkusAddToCart () {
      return Object.values(this.quantities).filter(i => i > 0).length
    },

    isPriceDiscoverable () {
      return (this.isBuyer && !this.isPreApprovalBuyer) || this.isHeadBuyer
    },

    groupVariantsByRegion () {
      const variants = this.variants.map(e => ({ ...e, FE_region_type: e.is_pod_grow_variant ? REGION_TYPES.PFG : e.region_type }))
      return groupBy(variants, 'FE_region_type')
    },

    submittable () {
      // Check MOQ to Disable Add to Cart button
      const listPfdItems = this.variants.filter(item => item.cartable && item.region_type === REGION_TYPES.PFD && !item.is_pod_grow_variant)

      if (!listPfdItems.length)
        return true

      if (this.estimatedQuantity(listPfdItems) > 0 && this.remainQty(listPfdItems) > 0)
        return false

      return true
    },

    onlyOneActiveSku () {
      return this.variants?.length === 1 && this.variants[0]?.cartable
    }
  },

  mounted () {
    this.initQuantities()
  },

  methods: {
    updateSelectedSku (sku) {
      this.resetQuantityBoxStyles()
      this.$set(this, 'selectedSku', sku)
      this.$emit('update:selectedId', this.variantId(sku))
    },

    initQuantities () {
      this.quantities = Object.values(this.variants).reduce((res, i) => ({
        ...res,
        [i.variants_region_id]: this.onlyOneActiveSku ? 1 : 0
      }), {})
    },

    resetQuantityBoxStyles () {
      const activeQuantityBox = this.$refs[`quantityBox-${this.variantId(this.selectedSku)}`]
      if (activeQuantityBox) {
        activeQuantityBox[0].classList.remove('adjust-warning')
      }
    },

    remainQty (variants) {
      const listMoqItems = variants.filter(i => i.limit_type === LIMIT_TYPES.MOQ && !i.is_pod_grow_variant)
      let moq = 1
      if (listMoqItems.length) {
        moq = (this.product?.products_region_moqs || [])
          .find(i => i.region_id === listMoqItems[0].region_id)?.moq || 1
      }

      return moq - this.estimatedQuantity(listMoqItems)
    },

    estimatedQuantity (variants) {
      if (!variants.length) return 0

      const listMoqItems = variants.filter(i => i.limit_type === LIMIT_TYPES.MOQ && !i.is_pod_grow_variant)
      const totalQuantity = listMoqItems.reduce((sum, v) => sum + this.quantities[v.variants_region_id], 0)

      if (!listMoqItems.length) return totalQuantity

      const addedQuantity = this.$store.getters['cart/addedQuantityByProduct']({ variant: variants[0] })

      return addedQuantity + totalQuantity
    },

    addToCart () {
      // Check Pre-Approval Buyer
      if (this.isPreApprovalBuyer) {
        this.$workflow.notify('Your account is currently under review. You will receive an email once your account is approved.', { type: 'warning' })
        return false
      }

      // Check Quantity & OOS (cartable)
      if (this.totalQuantitiesAddToCart <= 0) {
        if (!this.selectedSku.cartable) {
          this.$workflow.notify('This SKU is not available. Please select a different SKU.', { type: 'warning' })
          return false
        }

        // Animate shaking quantity box
        const activeQuantityBox = this.$refs[`quantityBox-${this.variantId(this.selectedSku)}`]
        if (activeQuantityBox) {
          activeQuantityBox[0].scrollIntoView({ block: 'center', behavior: 'smooth' })
          activeQuantityBox[0].classList.add('adjust-warning', 'horizontal-shaking')
          setTimeout(() => { activeQuantityBox[0].classList.remove('horizontal-shaking') }, 500)
        }

        this.$workflow.notify('Please enter a quantity greater than 0', { type: 'warning' })
        return false
      }

      this.$amplitude.track('CLICK_ADD_TO_CART', {
        zone: this.trackZone,
        quantity: this.totalQuantitiesAddToCart,
        skus: this.totalSkusAddToCart
      })
      this.adding = true

      const variants = Object
        .values(this.variants)
        .map(i => ({ ...i, promotions: i._promotions }))
        .filter(i => this.quantities[i.variants_region_id] > 0)
      const cartItems = Object
        .keys(this.quantities)
        .filter(id => this.quantities[id] > 0)
        .map(id => ({
          variants_region_id: id,
          quantity: this.quantities[id]
        }))

      this.$workflow.addMultipleItemToCart({
        parent: this,
        product: { ...this.product, variants: this.variants },
        variants,
        cartItems,
        events: { closed: () => { this.adding = false } }
      })
      .then(() => { this.initQuantities() })
      .catch(this.$debug.log)
      .finally(() => { this.adding = false })
    },

    requestSample () {
      return this.$workflow.requestSample({
        product: this.product
      })
    },

    caseUnits (variant) {
      const { case_units, product_variant_case_units } = variant
      return case_units || product_variant_case_units
    },

    imageUrl (variant) {
      const { image_urls, image_url, product_variant_small_image_url, primary_image_url, photo_url, logo_url, sku_image_url, product_image_url } = variant
      return image_urls?.thumbnail || image_url || product_variant_small_image_url || primary_image_url || photo_url || logo_url || sku_image_url || product_image_url
    },

    promotions (variant) {
      if (!variant) return []
      return variant.promotions || []
    },

    variantName (variant) {
      return variant.product_variant_name
        ? variant.product_variant_name
        : variant.name
    },

    unitPrice (variant) {
      return variant.unit_price_str ? variant.unit_price_str : variant.unit_price
    },

    variantId (variant) {
      return variant.product_variant_id || variant.id || variant.variants_region_id
    }
  }
}
</script>
<style lang="scss" scoped>
.multiple-skus-add-to-cart {
  &.plain {
    border: none;
    border-radius: none;
  }
  .moq-warning {
    .alert {
      .counter {
        font-size: 2em;
        line-height: 1em;
        font-weight: $--font-weight-bold;
        text-align: right;
        small {
          display: block;
          font-size: .5em;
          font-weight: $--font-weight-normal;
        }
      }
    }
  }
  .skus-list {
    .sku-item {
      position: relative;
      background: white;
      border: $--border-light;
      overflow: hidden;
      font-size: 12px;
      padding: 6px;
      &:not(:first-child) {
        border-top: 1px solid transparent;
      }
      &:first-child {
        border-top-left-radius: $--radius;
        border-top-right-radius: $--radius;
      }
      &:last-child {
        border-bottom-left-radius: $--radius;
        border-bottom-right-radius: $--radius;
      }
      &.selectable {
        cursor: pointer;
      }
      &.selected {
        border: 1px solid $--color-primary;
      }

      .general {
        display: flex;
        align-items: center;
        gap: 8px;
        @include media(xs-only) {
          flex-wrap: wrap;
          gap: 8px;
        }
        .image {
          min-width: 64px;
          flex: 0 64px;
          align-self: center;
          padding: 0;
          .contain {
            width: 100%;
            height: 0;
            padding-top: 100%;
            border-radius: $--radius;
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
          }
        }
        .preview {
          padding: 6px 6px 6px 2px;
          flex: 1;
          .name {
            color: $--color-text-regular;
            font-size: 13px;
          }
          .pack {
            color: $--color-text-secondary;
            margin: 2px 0;
          }
          .variant-regions {
            .region-item {
              .blocks {
                > * {
                  display: inline-block;
                  &:not(:last-child) {
                    margin-right: 0.9em;
                  }
                }
                .region {
                  min-width: 125px;
                }
                .original-price {
                  &.line-through {
                    text-decoration: line-through;
                  }
                }
                .discount-price {
                  font-weight: $--font-weight-bold;
                  color: $--color-primary;
                }
              }
            }
          }
          .info-variant {
            &__id {
              width: max-content;
              color: black;
              background: white;
              font-size: 11px;
              padding: 3px 5px;
              border-radius: $--radius;
              border: $--border-light;
            }
            &__name {
              font-size: 13px;
              margin-bottom: 3px;
            }
          }
        }

        .price {
          flex: 0 120px;
          .current {
            font-weight: $--font-weight-bold;
            font-size: 13px;
            &.promoted {
              color: $--color-primary;
            }
          }
          .old {
            font-size: 11px;
            color: $--color-text-secondary;
            text-decoration: line-through;
            margin-left: 8px;
          }
          .quick-promotion-tag {
            margin-top: 4px;
          }
        }

        .quantity-change {
          flex: 0 120px;
          border-radius: 6px;
          @include media(xs-only) {
            flex: 1 120px;
            margin-bottom: 12px;
          }
          &.horizontal-shaking {
            animation: horizontal-shaking 0.2s linear 2;
          }
          &.adjust-warning {
            ::v-deep {
              .el-input__inner { border: 1px solid $--color-primary; }
            }
          }
          .not-available {
            border: 1px solid #dcdfe6;
            border-radius: 6px;
            width: 120px;
            height: 32px;
            box-sizing: border-box;
            line-height: 32px;
            font-size: 13px;
            color: $--color-danger;
          }
        }
      }

      .promotion-explained {
        box-sizing: border-box;
      }

    }
  }

  .actions {
    position: sticky;
    position: -webkit-sticky;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    background: white;

    display: flex;
    align-items: center;
    gap: 25px;
    margin-top: 1em;
    padding: 12px 0;
    @include media(xs-only) {
      flex-direction: row-reverse;
      justify-content: space-between;
      background: white;
      border-top: $--border-light;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      z-index: 100;
      padding: 0;
      .add-to-cart {
        margin: 12px 15px 12px 0;
      }
      .sample-request {
        margin: 12px 0 12px 15px;
      }
    }
  }
}
</style>
